/**
 * AdminDropboxDocs — collega Dropbox, configura le cartelle da indicizzare e
 * cerca nei PDF (es. bonifici) su tutti i campi (testo + nome file).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Cloud, FolderOpen, Search, RefreshCw, Link2, Unlink, Plus, X,
  FileText, Loader2, CheckCircle2, AlertTriangle, ExternalLink, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import BackupStatusCard from "../components/BackupStatusCard";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; }
}

function fmtSize(bytes) {
  const n = Number(bytes || 0);
  if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n > 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

export default function AdminDropboxDocs() {
  const [status, setStatus] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/dropbox/status");
      setStatus(r.data);
    } catch { /* silente */ }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Toast esito OAuth callback
  useEffect(() => {
    const res = searchParams.get("dropbox");
    if (!res) return;
    if (res === "connected") toast.success("Dropbox collegato con successo");
    else toast.error(`Collegamento Dropbox fallito: ${searchParams.get("reason") || "errore"}`);
    const p = new URLSearchParams(searchParams);
    p.delete("dropbox"); p.delete("reason");
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  // Poll while syncing
  useEffect(() => {
    if (!status?.syncing) return undefined;
    const iv = setInterval(loadStatus, 6000);
    return () => clearInterval(iv);
  }, [status?.syncing, loadStatus]);

  if (!status) {
    return <div className="p-8 text-slate1 text-sm"><Loader2 size={16} className="inline animate-spin mr-2" />Caricamento…</div>;
  }

  return (
    <div className="space-y-6" data-testid="dropbox-docs-page">
      <div>
        <div className="text-[11px] tracking-[0.3em] uppercase text-gold inline-flex items-center gap-2">
          <Cloud size={13} /> Dropbox · Documenti indicizzati
        </div>
        <h2 className="font-display text-2xl text-ink mt-1">Ricerca documenti (bonifici, contabili…)</h2>
        <p className="text-sm text-slate1 mt-1 max-w-2xl">
          Collega Dropbox, scegli le cartelle da monitorare: i PDF vengono indicizzati su tutti i campi.
          Poi cerca qui sotto o chiedi direttamente all&apos;AI Agent (es. &quot;trova il bonifico a Flexform di marzo&quot;).
        </p>
      </div>

      <div className="flex justify-start">
        <BackupStatusCard />
      </div>

      {!status.connected && <SetupPanel status={status} onChange={loadStatus} />}
      {status.connected && (
        <>
          <ConnectionBar status={status} onChange={loadStatus} />
          <FoldersPanel status={status} onChange={loadStatus} />
          <SyncPanel status={status} onChange={loadStatus} />
          {status.indexed_count > 0 && <SearchPanel />}
          {status.docs_count > 0 && <IndexedDocsList />}
        </>
      )}
    </div>
  );
}

function SetupPanel({ status, onChange }) {
  const [appKey, setAppKey] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const save = async () => {
    if (!appKey.trim()) { toast.error("Inserisci l'App key"); return; }
    setSaving(true);
    try {
      await api.post("/admin/dropbox/credentials", { app_key: appKey.trim(), app_secret: appSecret.trim() });
      toast.success("Credenziali salvate");
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await api.get("/admin/dropbox/connect");
      window.location.href = r.data.authorization_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore avvio collegamento");
      setConnecting(false);
    }
  };

  return (
    <div className="bg-white border border-ink/10 rounded-sm p-5 space-y-4" data-testid="dropbox-setup-panel">
      <div className="font-display text-lg text-ink">1 · Configura l&apos;app Dropbox</div>
      <ol className="text-sm text-slate1 list-decimal ml-5 space-y-1.5">
        <li>Vai su <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noreferrer" className="text-gold underline">dropbox.com/developers/apps</a> → <strong>Create app</strong></li>
        <li>Scegli <strong>Scoped access</strong> → <strong>Full Dropbox</strong> → dai un nome (es. &quot;ARCHI-HUB Docs&quot;)</li>
        <li>Tab <strong>Permissions</strong>: spunta <code className="bg-cream2 px-1">files.metadata.read</code> e <code className="bg-cream2 px-1">files.content.read</code> → Submit</li>
        <li>Tab <strong>Settings</strong> → sezione <strong>OAuth 2 · Redirect URIs</strong>: aggiungi
          <code className="block bg-cream2 px-2 py-1 mt-1 text-xs break-all select-all">{status.callback_url}</code>
        </li>
        <li>Copia <strong>App key</strong> e <strong>App secret</strong> qui sotto</li>
      </ol>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">App key</label>
          <input value={appKey} onChange={(e) => setAppKey(e.target.value)} placeholder={status.configured ? "•••••• (già salvata)" : ""} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="dropbox-app-key" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">App secret</label>
          <input type="password" value={appSecret} onChange={(e) => setAppSecret(e.target.value)} placeholder={status.configured ? "•••••• (già salvato)" : ""} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="dropbox-app-secret" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={save} disabled={saving} className="bg-ink text-cream px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm hover-lift disabled:opacity-50" data-testid="dropbox-save-credentials">
          {saving ? "Salvataggio…" : "Salva credenziali"}
        </button>
        {status.configured && (
          <button type="button" onClick={connect} disabled={connecting} className="inline-flex items-center gap-2 bg-gold text-ink px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm hover-lift disabled:opacity-50" data-testid="dropbox-connect-btn">
            <Link2 size={13} /> {connecting ? "Reindirizzamento…" : "2 · Collega Dropbox"}
          </button>
        )}
      </div>
    </div>
  );
}

function ConnectionBar({ status, onChange }) {
  const disconnect = async () => {
    if (!window.confirm("Scollegare Dropbox? Le cartelle e l'indice restano, ma sync e ricerca link si fermano.")) return;
    try {
      await api.post("/admin/dropbox/disconnect");
      toast.success("Dropbox scollegato");
      onChange();
    } catch { toast.error("Errore"); }
  };
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="dropbox-connection-bar">
      <div className="inline-flex items-center gap-2 text-sm text-emerald-900">
        <CheckCircle2 size={15} className="text-emerald-600" />
        Collegato come <strong>{status.account_name || status.account_email}</strong>
        {status.account_email && <span className="text-emerald-700">· {status.account_email}</span>}
      </div>
      <button type="button" onClick={disconnect} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-slate-600 hover:text-rose-700 transition" data-testid="dropbox-disconnect-btn">
        <Unlink size={12} /> Scollega
      </button>
    </div>
  );
}

function FoldersPanel({ status, onChange }) {
  const [browsePath, setBrowsePath] = useState("");
  const [subFolders, setSubFolders] = useState(null);
  const [loadingBrowse, setLoadingBrowse] = useState(false);
  const [showBrowser, setShowBrowser] = useState(false);
  const [manualPath, setManualPath] = useState("");

  const browse = async (path) => {
    setLoadingBrowse(true);
    try {
      const r = await api.get("/admin/dropbox/browse", { params: { path } });
      setBrowsePath(path);
      setSubFolders(r.data.folders || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore Dropbox");
    } finally { setLoadingBrowse(false); }
  };

  const saveFolders = async (folders) => {
    try {
      await api.put("/admin/dropbox/folders", { folders });
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore salvataggio cartelle");
    }
  };

  const addFolder = async (path) => {
    if (status.folders.includes(path)) { toast("Cartella già monitorata"); return; }
    try {
      const v = await api.get("/admin/dropbox/validate", { params: { path } });
      const validPath = v.data.path || path;
      await saveFolders([...status.folders, validPath]);
      toast.success(`Cartella verificata e aggiunta: ${validPath}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Cartella non trovata su Dropbox", { duration: 8000 });
    }
  };

  const removeFolder = async (path) => {
    await saveFolders(status.folders.filter((f) => f !== path));
  };

  return (
    <div className="bg-white border border-ink/10 rounded-sm p-5 space-y-3" data-testid="dropbox-folders-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-display text-lg text-ink inline-flex items-center gap-2"><FolderOpen size={16} /> Cartelle monitorate</div>
        <button
          type="button"
          onClick={() => { setShowBrowser((v) => !v); if (!showBrowser) browse(""); }}
          className="inline-flex items-center gap-1.5 border border-ink/20 px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase rounded-sm hover:bg-ink hover:text-cream transition"
          data-testid="dropbox-browse-toggle"
        >
          <Plus size={12} /> {showBrowser ? "Chiudi" : "Aggiungi cartella"}
        </button>
      </div>

      {status.folders.length === 0 ? (
        <p className="text-sm text-slate1">Nessuna cartella. Aggiungi le cartelle Dropbox dove stanno i PDF (es. /Contabilità/Bonifici).</p>
      ) : (
        <ul className="space-y-1.5">
          {status.folders.map((f) => (
            <li key={f} className="flex items-center justify-between bg-cream2 px-3 py-2 rounded-sm text-sm" data-testid={`dropbox-folder-${f}`}>
              <span className="font-mono text-xs text-ink">{f}</span>
              <button type="button" onClick={() => removeFolder(f)} className="p-1 text-slate-400 hover:text-rose-600 transition" title="Rimuovi" data-testid={`dropbox-folder-remove-${f}`}>
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {showBrowser && (
        <div className="flex flex-wrap items-center gap-2" data-testid="dropbox-manual-add">
          <input
            value={manualPath}
            onChange={(e) => setManualPath(e.target.value)}
            placeholder="Oppure scrivi il percorso: /Contabilità/Bonifici"
            className="flex-1 min-w-[240px] border border-ink/20 px-3 py-2 rounded-sm bg-cream text-sm font-mono"
            data-testid="dropbox-manual-path"
            onKeyDown={(e) => { if (e.key === "Enter" && manualPath.trim()) { addFolder(manualPath.trim().startsWith("/") ? manualPath.trim() : "/" + manualPath.trim()); setManualPath(""); } }}
          />
          <button
            type="button"
            disabled={!manualPath.trim()}
            onClick={() => { addFolder(manualPath.trim().startsWith("/") ? manualPath.trim() : "/" + manualPath.trim()); setManualPath(""); }}
            className="bg-ink text-cream px-4 py-2 text-[11px] tracking-[0.15em] uppercase rounded-sm disabled:opacity-40"
            data-testid="dropbox-manual-add-btn"
          >
            Aggiungi percorso
          </button>
        </div>
      )}

      {showBrowser && (
        <div className="border border-ink/10 rounded-sm p-3 bg-cream/50" data-testid="dropbox-browser">
          <div className="flex items-center gap-2 text-xs text-slate1 mb-2 flex-wrap">
            <button type="button" onClick={() => browse("")} className="underline hover:text-ink">Dropbox</button>
            {browsePath.split("/").filter(Boolean).map((seg, i, arr) => {
              const p = "/" + arr.slice(0, i + 1).join("/");
              return (
                <span key={p} className="inline-flex items-center gap-1">
                  <ChevronRight size={11} />
                  <button type="button" onClick={() => browse(p)} className="underline hover:text-ink">{seg}</button>
                </span>
              );
            })}
            {browsePath && (
              <button type="button" onClick={() => addFolder(browsePath)} className="ml-auto bg-gold text-ink px-2.5 py-1 rounded-sm text-[10px] tracking-[0.15em] uppercase font-semibold" data-testid="dropbox-add-current">
                + Monitora questa cartella
              </button>
            )}
          </div>
          {loadingBrowse ? (
            <div className="text-sm text-slate1 py-2"><Loader2 size={13} className="inline animate-spin mr-1.5" />Caricamento…</div>
          ) : subFolders && subFolders.length === 0 ? (
            <div className="text-sm text-slate1 py-2">Nessuna sottocartella</div>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-1">
              {(subFolders || []).map((f) => (
                <li key={f.path} className="flex items-center justify-between px-2 py-1.5 hover:bg-white rounded-sm text-sm">
                  <button type="button" onClick={() => browse(f.path)} className="inline-flex items-center gap-2 text-ink hover:text-gold transition" data-testid={`dropbox-browse-into-${f.name}`}>
                    <FolderOpen size={13} className="text-gold" /> {f.name}
                  </button>
                  <button type="button" onClick={() => addFolder(f.path)} className="text-[10px] tracking-[0.1em] uppercase text-slate1 hover:text-ink border border-ink/15 px-2 py-0.5 rounded-sm" data-testid={`dropbox-add-${f.name}`}>
                    + monitora
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function SyncPanel({ status, onChange }) {
  const [starting, setStarting] = useState(false);
  const startSync = async () => {
    setStarting(true);
    try {
      const r = await api.post("/admin/dropbox/sync");
      if (r.data.started) toast.success("Sincronizzazione avviata");
      else toast(r.data.message || "Sync già in corso");
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore avvio sync");
    } finally { setStarting(false); }
  };

  return (
    <div className="bg-white border border-ink/10 rounded-sm p-5" data-testid="dropbox-sync-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-lg text-ink inline-flex items-center gap-2">
            <RefreshCw size={15} className={status.syncing ? "animate-spin text-gold" : ""} /> Indicizzazione
          </div>
          <div className="text-xs text-slate1 mt-1">
            {status.docs_count} PDF · {status.indexed_count} indicizzati · {status.pages_count} pagine
            {status.error_count > 0 && <span className="text-rose-600"> · {status.error_count} errori</span>}
          </div>
          <div className="text-[11px] text-slate1 mt-0.5">
            Ultima sync: {fmtDate(status.last_sync_at)} {status.last_sync_result && <>· {status.last_sync_result}</>} · Cron automatico ogni notte alle 02:30
          </div>
          {(status.last_sync_errors || []).length > 0 && (
            <div className="mt-2 bg-rose-50 border border-rose-200 rounded-sm p-2.5 space-y-1" data-testid="dropbox-sync-errors">
              <div className="text-[10px] tracking-[0.15em] uppercase text-rose-700 inline-flex items-center gap-1.5">
                <AlertTriangle size={11} /> Dettaglio errori ultima sync
              </div>
              {status.last_sync_errors.map((e, i) => (
                <div key={i} className="text-xs text-rose-800">{e}</div>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={startSync}
          disabled={starting || status.syncing || status.folders.length === 0}
          className="inline-flex items-center gap-2 bg-ink text-cream px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm hover-lift disabled:opacity-50"
          data-testid="dropbox-sync-now"
        >
          {status.syncing ? <><Loader2 size={13} className="animate-spin" /> In corso…</> : <><RefreshCw size={13} /> Sincronizza ora</>}
        </button>
      </div>
    </div>
  );
}

function SearchPanel() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const runSearch = useCallback((term) => {
    if (abortRef.current) abortRef.current.abort();
    if (!term || term.trim().length < 2) { setResults(null); setLoading(false); return; }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    api.get("/admin/dropbox/search", { params: { q: term.trim() }, signal: controller.signal })
      .then((r) => setResults(r.data))
      .catch((err) => { if (err.name !== "CanceledError") toast.error("Errore ricerca"); })
      .finally(() => setLoading(false));
  }, []);

  const onChange = (v) => {
    setQ(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 350);
  };

  const openDoc = async (docId) => {
    try {
      const r = await api.get(`/admin/dropbox/docs/${docId}/link`);
      window.open(r.data.link, "_blank", "noopener");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore apertura PDF");
    }
  };

  const highlight = (text) => {
    if (!results?.query) return text;
    const tokens = results.query.split(/\s+/).filter((t) => t.length >= 2);
    if (!tokens.length) return text;
    const rx = new RegExp(`(${tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "ig");
    return text.split(rx).map((part, i) =>
      rx.test(part) ? <mark key={i} className="bg-gold/40 text-ink px-0.5 rounded-sm">{part}</mark> : part
    );
  };

  return (
    <div className="bg-ink rounded-sm p-5 relative overflow-hidden" data-testid="dropbox-search-panel">
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-gold/15 rounded-full blur-3xl pointer-events-none" />
      <div className="text-[11px] tracking-[0.3em] uppercase text-gold inline-flex items-center gap-2 mb-3">
        <Search size={13} /> Ricerca nei documenti
      </div>
      <input
        value={q}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Es: bonifico Flexform 2.400 · beneficiario · CRO · causale…"
        className="w-full bg-white/10 border border-gold/30 text-cream placeholder:text-cream/40 px-4 py-3 rounded-sm outline-none focus:border-gold"
        data-testid="dropbox-search-input"
      />
      {loading && <div className="text-cream/60 text-xs mt-2"><Loader2 size={12} className="inline animate-spin mr-1.5" />Ricerca…</div>}
      {results && !loading && (
        <div className="mt-4 space-y-3">
          {results.results.length === 0 ? (
            <div className="text-cream/60 text-sm" data-testid="dropbox-search-empty">Nessun documento trovato per “{results.query}”</div>
          ) : results.results.map((r) => (
            <div key={r.doc_id} className="bg-white/5 border border-white/10 rounded-sm p-3" data-testid={`dropbox-result-${r.doc_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="inline-flex items-center gap-2 text-cream text-sm font-medium">
                  <FileText size={14} className="text-gold" /> {r.name}
                  <span className="text-cream/40 text-[10px]">{fmtSize(r.size)}</span>
                </div>
                <button type="button" onClick={() => openDoc(r.doc_id)} className="inline-flex items-center gap-1.5 bg-gold text-ink px-3 py-1.5 rounded-sm text-[10px] tracking-[0.15em] uppercase font-semibold hover:bg-amber-400 transition" data-testid={`dropbox-open-${r.doc_id}`}>
                  <ExternalLink size={11} /> Apri PDF
                </button>
              </div>
              <div className="text-[10px] text-cream/40 font-mono mt-0.5">{r.path_display}</div>
              {r.matches.map((m) => (
                <div key={m.page} className="mt-2 text-xs text-cream/80 leading-relaxed">
                  <span className="inline-block bg-white/10 text-gold px-1.5 py-0.5 rounded-sm text-[9px] tracking-[0.15em] uppercase mr-2">Pag. {m.page}</span>
                  {highlight(m.snippet)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IndexedDocsList() {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const debounceRef = useRef(null);

  const load = useCallback(async (q) => {
    try {
      const r = await api.get("/admin/dropbox/docs", { params: { q: q || "", limit: 60 } });
      setDocs(r.data.docs || []);
      setTotal(r.data.total || 0);
    } catch { /* silente */ }
  }, []);

  useEffect(() => { if (open) load(""); }, [open, load]);

  const onFilter = (v) => {
    setFilter(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(v), 350);
  };

  return (
    <div className="bg-white border border-ink/10 rounded-sm p-5" data-testid="dropbox-docs-list-panel">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between" data-testid="dropbox-docs-list-toggle">
        <span className="font-display text-lg text-ink inline-flex items-center gap-2"><FileText size={15} /> PDF nell&apos;indice</span>
        <span className="text-[11px] tracking-[0.15em] uppercase text-slate1">{open ? "Chiudi" : "Mostra"}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <input
            value={filter}
            onChange={(e) => onFilter(e.target.value)}
            placeholder="Filtra per nome file (es. tendatech)…"
            className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream text-sm"
            data-testid="dropbox-docs-list-filter"
          />
          <div className="text-[11px] text-slate1">{total} PDF {filter && "corrispondenti"} nell&apos;indice</div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-ink/5">
            {docs.map((d) => (
              <div key={d.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid={`dropbox-doc-row-${d.id}`}>
                <FileText size={13} className="text-gold shrink-0" />
                <span className="text-ink">{d.name}</span>
                <span className="text-[10px] text-slate1 font-mono">{d.folder}</span>
                {d.indexed_status === "indexed" && d.has_text !== false && (
                  <span className="text-[9px] tracking-[0.12em] uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-sm">{d.indexed_pages || 0} pag. indicizzate</span>
                )}
                {d.indexed_status === "indexed" && d.has_text === false && (
                  <span className="text-[9px] tracking-[0.12em] uppercase bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-sm" title="PDF scansionato/immagine: il testo interno non è leggibile, la ricerca funziona solo sul nome del file">solo nome file (scansione)</span>
                )}
                {d.indexed_status === "error" && (
                  <span className="text-[9px] tracking-[0.12em] uppercase bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-sm" title={d.index_error || ""}>errore</span>
                )}
                {d.indexed_status === "skipped" && (
                  <span className="text-[9px] tracking-[0.12em] uppercase bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded-sm">saltato &gt;50MB</span>
                )}
              </div>
            ))}
            {docs.length === 0 && <div className="py-3 text-sm text-slate1">Nessun PDF{filter ? ` per “${filter}”` : " nell'indice"}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
